/**
 * Switch Database Index
 *
 * Central index for all switch models with lookup and filter functions.
 * Merges built-in switches with custom switches from localStorage.
 */

import type { SwitchModel, SwitchRole, SwitchFabricType } from '../../types/switches';
import { getCustomSwitchProfilesSync } from '../../services/customProfileStorage';
import { NVIDIA_INFINIBAND_SWITCHES } from './nvidia-infiniband';
import { NVIDIA_ETHERNET_SWITCHES } from './nvidia-ethernet';
import { CISCO_SWITCHES } from './cisco';
import { CELESTICA_SWITCHES } from './celestica';
import { DELL_SWITCHES } from './dell';
import { ARISTA_SWITCHES } from './arista';

/**
 * Built-in switches from all vendors
 */
export const BUILTIN_SWITCHES: readonly SwitchModel[] = [
  ...NVIDIA_INFINIBAND_SWITCHES,
  ...NVIDIA_ETHERNET_SWITCHES,
  ...CISCO_SWITCHES,
  ...CELESTICA_SWITCHES,
  ...DELL_SWITCHES,
  ...ARISTA_SWITCHES,
] as const;

/**
 * Get all switches including custom ones from localStorage
 */
function getAllSwitchesWithCustom(): SwitchModel[] {
  const customSwitches = getCustomSwitchProfilesSync();
  return [...BUILTIN_SWITCHES, ...customSwitches];
}

/**
 * All switches from all vendors (built-in + custom)
 */
export const ALL_SWITCHES: SwitchModel[] = getAllSwitchesWithCustom();

/**
 * Get switch by ID (checks built-in + custom)
 */
export function getSwitchById(id: string): SwitchModel | undefined {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.find((sw) => sw.id === id);
}

/**
 * Get switches by vendor (includes custom)
 */
export function getSwitchesByVendor(vendor: string): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => sw.vendor === vendor);
}

/**
 * Get switches by fabric type (includes custom)
 */
export function getSwitchesByFabricType(fabricType: SwitchFabricType): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => sw.fabricType === fabricType);
}

/**
 * Get switches by role (includes custom)
 */
export function getSwitchesByRole(role: SwitchRole): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => sw.typicalRoles.includes(role));
}

/**
 * Get switches by port speed (includes custom)
 */
export function getSwitchesBySpeed(speedGbps: number): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => sw.ports.speed.value === speedGbps);
}

/**
 * Get switches by minimum port count (includes custom)
 */
export function getSwitchesByMinPorts(minPorts: number): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => sw.ports.totalPorts >= minPorts);
}

/**
 * Get switches by rack units (for density calculations, includes custom)
 */
export function getSwitchesByRackUnits(maxRU: number): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => sw.physical.rackUnits <= maxRU);
}

/**
 * Switch filter options
 */
export interface SwitchFilterOptions {
  vendor?: string;
  fabricType?: SwitchFabricType;
  role?: SwitchRole;
  minSpeed?: number;
  maxSpeed?: number;
  minPorts?: number;
  maxPorts?: number;
  maxRackUnits?: number;
  maxPowerWatts?: number;
}

/**
 * Filter switches by multiple criteria (includes custom)
 */
export function filterSwitches(options: SwitchFilterOptions): SwitchModel[] {
  const allSwitches = getAllSwitchesWithCustom();
  return allSwitches.filter((sw) => {
    if (options.vendor && sw.vendor !== options.vendor) return false;
    if (options.fabricType && sw.fabricType !== options.fabricType) return false;
    if (options.role && !sw.typicalRoles.includes(options.role)) return false;
    if (options.minSpeed && sw.ports.speed.value < options.minSpeed) return false;
    if (options.maxSpeed && sw.ports.speed.value > options.maxSpeed) return false;
    if (options.minPorts && sw.ports.totalPorts < options.minPorts) return false;
    if (options.maxPorts && sw.ports.totalPorts > options.maxPorts) return false;
    if (options.maxRackUnits && sw.physical.rackUnits > options.maxRackUnits) return false;
    if (options.maxPowerWatts && sw.power.maxWatts > options.maxPowerWatts) return false;
    return true;
  });
}

/**
 * Sort switches by various criteria
 */
export type SwitchSortCriteria =
  | 'speed-desc'
  | 'speed-asc'
  | 'ports-desc'
  | 'ports-asc'
  | 'power-asc'
  | 'power-desc'
  | 'density-desc'; // Ports per RU

/**
 * Sort switches
 */
export function sortSwitches(
  switches: readonly SwitchModel[],
  criteria: SwitchSortCriteria
): readonly SwitchModel[] {
  const sorted = [...switches];

  switch (criteria) {
    case 'speed-desc':
      sorted.sort((a, b) => b.ports.speed.value - a.ports.speed.value);
      break;
    case 'speed-asc':
      sorted.sort((a, b) => a.ports.speed.value - b.ports.speed.value);
      break;
    case 'ports-desc':
      sorted.sort((a, b) => b.ports.totalPorts - a.ports.totalPorts);
      break;
    case 'ports-asc':
      sorted.sort((a, b) => a.ports.totalPorts - b.ports.totalPorts);
      break;
    case 'power-asc':
      sorted.sort((a, b) => a.power.typicalWatts - b.power.typicalWatts);
      break;
    case 'power-desc':
      sorted.sort((a, b) => b.power.typicalWatts - a.power.typicalWatts);
      break;
    case 'density-desc':
      sorted.sort((a, b) => {
        const densityA = a.ports.totalPorts / a.physical.rackUnits;
        const densityB = b.ports.totalPorts / b.physical.rackUnits;
        return densityB - densityA;
      });
      break;
  }

  return sorted;
}

/**
 * Get recommended switch for specific requirements
 */
export interface SwitchRecommendationParams {
  fabricType: SwitchFabricType;
  role: SwitchRole;
  speedGbps: number;
  nodeCount?: number;
  preferVendor?: string;
}

/**
 * Recommend a switch based on requirements
 */
export function recommendSwitch(params: SwitchRecommendationParams): SwitchModel | undefined {
  const { fabricType, role, speedGbps, nodeCount, preferVendor } = params;

  // Filter candidates
  let candidates = filterSwitches({
    fabricType,
    role,
    minSpeed: speedGbps * 0.9, // Allow 10% tolerance
    maxSpeed: speedGbps * 2, // Up to 2x speed
  });

  if (candidates.length === 0) {
    return undefined;
  }

  // Prefer specific vendor if requested
  if (preferVendor) {
    const vendorMatches = candidates.filter((sw) => sw.vendor === preferVendor);
    if (vendorMatches.length > 0) {
      candidates = vendorMatches;
    }
  }

  // If we have node count, prefer switches with appropriate port count
  if (nodeCount) {
    const estimatedPortsNeeded = Math.ceil(nodeCount / 2); // Rough estimate
    candidates = sortSwitches(candidates, 'ports-desc').filter(
      (sw) => sw.ports.totalPorts >= estimatedPortsNeeded
    );
  }

  // Sort by speed (prefer exact match)
  const exactSpeedMatches = candidates.filter((sw) => sw.ports.speed.value === speedGbps);
  if (exactSpeedMatches.length > 0) {
    candidates = exactSpeedMatches;
  }

  // Prefer newer switches (check release year)
  candidates.sort((a, b) => {
    const yearA = a.metadata?.releaseYear || 2020;
    const yearB = b.metadata?.releaseYear || 2020;
    return yearB - yearA;
  });

  // Return the best match
  return candidates[0];
}

/**
 * Calculate port density (ports per rack unit)
 */
export function calculatePortDensity(switchModel: SwitchModel): number {
  return switchModel.ports.totalPorts / switchModel.physical.rackUnits;
}

/**
 * Calculate power per port
 */
export function calculatePowerPerPort(switchModel: SwitchModel): number {
  return switchModel.power.typicalWatts / switchModel.ports.totalPorts;
}

/**
 * Calculate total switching capacity for a given count
 */
export function calculateTotalSwitchingCapacity(
  switchModel: SwitchModel,
  count: number
): number {
  return switchModel.performance.switchingCapacityTbps * count;
}

/**
 * Calculate total power for a given count
 */
export function calculateTotalPower(
  switchModel: SwitchModel,
  count: number,
  utilizationPercent: number = 100
): { typical: number; max: number } {
  const utilization = utilizationPercent / 100;
  return {
    typical: switchModel.power.typicalWatts * count * utilization,
    max: switchModel.power.maxWatts * count * utilization,
  };
}

/**
 * Calculate total rack units for a given count
 */
export function calculateTotalRackUnits(switchModel: SwitchModel, count: number): number {
  return switchModel.physical.rackUnits * count;
}

/**
 * Get InfiniBand switches only
 */
export function getInfiniBandSwitches(): readonly SwitchModel[] {
  return getSwitchesByFabricType('InfiniBand');
}

/**
 * Get Ethernet switches only
 */
export function getEthernetSwitches(): readonly SwitchModel[] {
  return getSwitchesByFabricType('Ethernet');
}

/**
 * Get RoCE switches only
 */
export function getRoCESwitches(): readonly SwitchModel[] {
  return getSwitchesByFabricType('RoCE');
}

/**
 * Get management switches (OOB)
 */
export function getManagementSwitches(): readonly SwitchModel[] {
  return getSwitchesByRole('management');
}

/**
 * Export switch database collections
 */
export {
  NVIDIA_INFINIBAND_SWITCHES,
  NVIDIA_ETHERNET_SWITCHES,
  CISCO_SWITCHES,
  CELESTICA_SWITCHES,
  DELL_SWITCHES,
  ARISTA_SWITCHES,
};

/**
 * Export individual switch getters
 */
export { getNvidiaInfiniBandSwitch, getRecommendedNvidiaInfiniBandSwitch } from './nvidia-infiniband';
export { getNvidiaEthernetSwitch, getRecommendedNvidiaEthernetSwitch } from './nvidia-ethernet';
export { getCiscoSwitch } from './cisco';
export { getCelesticaSwitch } from './celestica';
export { getDellSwitch } from './dell';
export { getAristaSwitch } from './arista';
